"use strict";

//const _ = require('lodash');
const GridColumn = require('./column/GridColumnComponent');
const GridRow = require('./row/GridRowComponent');
const BrowserDetect = require('@gluenetworks/srvc_core_browser-detect');
const StylesheetUtility = require('./utilities/DynamicStylesheetUtility');
const ElementResizeDetectionUtility = require('./utilities/ElementResizeDetectionUtility');
const _ = require('lodash');

class GridRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("grid-gl");
        this._rootElem.component = this._model;
        this._headerRow = document.createElement("header-row");
        this._gridBody = document.createElement("grid-body");
        this._rootElem.appendChild(this._headerRow);
        this._rootElem.appendChild(this._gridBody);

        this._columnComponents = {};
        this._rowComponents = {};
        this._hasCentralColumn = false;
        
        this._lastRowSelectionId = undefined;
        this._commandKeyDown = false;
        this._sortColumn = undefined;
        this._sortFunction = undefined;
        this._sort = undefined;

        this._registerListeners();
        this.render();
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _shutdown(){
        this._unregisterListeners();
        let promArr = [];

        for (let colKey in this._columnComponents) {
            this._columnComponents[colKey].removeAllListeners('UserSorted');
            promArr.push(this._columnComponents[colKey].shutdown());
        }
        for (let rowKey in this._rowComponents) {
            this._rowComponents[rowKey].removeAllListeners('RowUserSelected');
            this._rowComponents[rowKey].removeAllListeners('RowUserDoubleClicked');
            this._rowComponents[rowKey].removeAllListeners('RowAction');
            promArr.push(this._rowComponents[rowKey].shutdown());
        }

        return Promise.all(promArr)
            .then(()=>{
                this._columnComponents = undefined;
                this._headerRow.remove();
                this._headerRow = undefined;
                this._gridBody.remove();
                this._gridBody = undefined;
                this._hasCentralColumn = undefined;
                this._rowComponents = undefined;
                this._lastRowSelectionId = undefined;
                this._commandKeyDown = undefined;
                this._sortColumn = undefined;
                this._sortFunction = undefined;
                this._sort = undefined;
                this._model = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
            });
    }
    _registerListeners(){
        this._model.addListener("Id",this._onId = this._onId.bind(this));
        this._model.addListener("Rows",this._onRows = this._onRows.bind(this));
        this._model.addListener("AddRow",this._onAddRow = this._onAddRow.bind(this));
        this._model.addListener("UpdateRow",this._onUpdateRow = this._onUpdateRow.bind(this));
        this._model.addListener("DeleteRow",this._onDeleteRow = this._onDeleteRow.bind(this));
        this._model.addListener("SortRows",this._onSortRows = this._onSortRows.bind(this));
        this._model.addListener("Columns",this._onColumns = this._onColumns.bind(this));
        this._model.addListener("ColumnOrder",this._onColumnOrder = this._onColumnOrder.bind(this));
        this._model.addListener("ShowHeader",this._onShowHeader = this._onShowHeader.bind(this));
        this._model.addListener("SelectedRows",this._onSelectedRows = this._onSelectedRows.bind(this));
        this._model.addListener("Focus",this._onFocus = this._onFocus.bind(this));
        this._model.addListener("Blur",this._onBlur = this._onBlur.bind(this));
        this._rootElem.addEventListener('focus', this._onFocusEvent = this._onFocusEvent.bind(this));
        this._rootElem.addEventListener('blur', this._onBlurEvent = this._onBlurEvent.bind(this));
        ElementResizeDetectionUtility().addResizeListener(this._rootElem, this._onResize = this._onResize.bind(this));
        if (BrowserDetect.os === 'Mac') {
            window.addEventListener('keydown', this._onKeyDown = this._onKeyDown.bind(this));
            window.addEventListener('keyup', this._onKeyUp = this._onKeyUp.bind(this));
        }
    }
    _unregisterListeners(){
        this._model.removeListener("Id",this._onId);
        this._model.removeListener("Rows",this._onRows);
        this._model.removeListener("AddRow",this._onAddRow);
        this._model.removeListener("UpdateRow",this._onUpdateRow);
        this._model.removeListener("DeleteRow",this._onDeleteRow);
        this._model.removeListener("SortRows",this._onSortRows);
        this._model.removeListener("Columns",this._onColumns);
        this._model.removeListener("ColumnOrder",this._onColumnOrder);
        this._model.removeListener("ShowHeader",this._onShowHeader);
        this._model.removeListener("SelectedRows",this._onSelectedRows);
        this._model.removeListener("Focus",this._onFocus);
        this._model.removeListener("Blur",this._onBlur);
        this._rootElem.removeEventListener('focus', this._onFocusEvent);
        this._rootElem.removeEventListener('blur', this._onBlurEvent);
        ElementResizeDetectionUtility().removeResizeListener(this._rootElem, this._onResize);
        if (BrowserDetect.os === 'Mac') {
            window.removeEventListener('keydown', this._onKeyDown);
            window.removeEventListener('keyup', this._onKeyUp);
        }
    }
    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render(){
        let prom = new Promise((resolve/*,reject*/) => {
            this._headerRow.innerHTML = '';
            this._gridBody.innerHTML = '';
            this._setId(this._model.getId());
            this._setColumns(this._model.getColumns());
            this._setColumnOrder(this._model.getColumnOrder());
            let rows = this._model.getRows();
            this._setRows(rows);
            this._setShowHeader(this._model.getShowHeader());
            this._setSelectedRows(this._model.getSelectedRows());
            if (!_.isUndefined(this._sortColumn)) {
                this._setGridSorted(this._columnComponents[this._sortColumn]);
            }
            if (this._hasCentralColumn === true && Object.keys(rows).length > 0) {
                this._setCentralColumnWidth();
            }
            resolve();
        });
        return prom;
    }
    get rootElement(){
        return this.getRootElement();
    }
    getRootElement(){
        return this._rootElem;
    }
    get model(){
        return this.getModel();
    }
    getModel(){
        return this._model;
    }
    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /**
     * Fired when the id has been updated in the model
     * @param newVal {string}
     * @private
     */
    _onId(newVal){
        this._setId(newVal);
    }

    /**
     * Fired when the column configuration has been updated in the model
     * @param newVal {object}
     * @private
     */
    _onColumns(newVal){
        this._setColumns(newVal);
    }

    /**
     * Fired when the column order has been updated in the model
     * @param newVal {array}
     * @private
     */
    _onColumnOrder(newVal){
        this._setColumnOrder(newVal);
    }

    /**
     * Fired when the row data has been updated in the model
     * @param newVal {object}
     * @private
     */
    _onRows(newVal){
        this._setRows(newVal, true);
        if (this._hasCentralColumn === true && Object.keys(newVal).length > 0) {
            this._setCentralColumnWidth();
        }
    }

    /**
     * Fired when a new row has been added to the model
     * @param newVal {object}
     * @private
     */
    _onAddRow(newVal){
        this._addRow(newVal, true);
        if (this._hasCentralColumn === true) {
            this._setCentralColumnWidth();
        }
    }

    /**
     * Fired when a row has been updated in the model
     * @param newVal {object}
     * @private
     */
    _onUpdateRow(newVal){
        this._updateRow(newVal);
        if (this._hasCentralColumn === true) {
            this._setCentralColumnWidth();
        }
    }

    /**
     * Fired when a row has been deleted in the model
     * @param rowKey {string}
     * @private
     */
    _onDeleteRow(rowKey){
        this._deleteRow(rowKey);
        let rows = this._model.getRows();
        if (this._hasCentralColumn === true && Object.keys(rows).length > 0) {
            this._setCentralColumnWidth();
        }
    }

    /**
     * Fired when a sort has been triggered programatically
     * @private
     */
    _onSortRows(){
        this._sortRows();
    }

    /**
     * Fired when the selcted rows have been updated in the model
     * @param newVal {array}
     * @private
     */
    _onSelectedRows(newVal){
        this._setSelectedRows(newVal);
    }

    /**
     * Fired when the display of the header has been updated in the model
     * @param newVal {boolean}
     * @private
     */
    _onShowHeader(newVal){
        this._setShowHeader(newVal);
    }

    /**
     * Fired when focus has been called in the model
     * @private
     */
    _onFocus(){
        this._setFocus(true);
    }

    /**
     * Fired when blur has been called in the model
     * @private
     */
    _onBlur(){
        this._setFocus(false);
    }

    /**
     * Pass the focus event up to the model
     * @param event {Event}
     * @private
     */
    _onFocusEvent(/*event*/){
        //this._model._triggerUserFocus(event);
    }

    /**
     * Pass the blur event up to the model
     * @param event {Event}
     * @private
     */
    _onBlurEvent(/*event*/){
        //this._model._triggerUserBlur(event);
    }

    /**
     * Pass the resize event for the grid up to the model
     * @param event {Event}
     * @private
     */
    _onResize(event){
        this._model._triggerGridResized(event);
    }

    /**
     * Catch the command key
     * @param event {Event}
     * @private
     */
    _onKeyDown(event){
        if (event.keyCode === 91 || event.keyCode === 93) {
            this._commandKeyDown = true;
        } else {
            this._commandKeyDown = false;
        }
    }

    /**
     * Catch the command key
     * @param event {event}
     * @private
     */
    _onKeyUp(event){
        if (event.keyCode === 91 || event.keyCode === 93) {
            this._commandKeyDown = false;
        }
    }

    /**
     * Fired when a column has been sorted by the user
     * @param sorted {string}
     * @param column {component}
     * @private
     */
    _onColumnUserSorted(sorted, column){
        this._setGridSorted(column);
    }

    /**
     * Fired when the user has selected a row
     * @param rowData {object}
     * @param event {event}
     * @private
     */
    _onRowUserSelected(rowData, event){
        let selectedRows = this._model.getSelectedRows();
        let currentRowSelectionId = event.currentTarget.component.getId();
        let singleSelect = this._model.getSingleSelect();
        
        if (singleSelect === true) {
            if (event.ctrlKey === true || this._commandKeyDown === true) { // Control/Command Click
                if (selectedRows[0] === currentRowSelectionId) {
                    selectedRows = [];
                } else {
                    selectedRows = [currentRowSelectionId];
                }
            } else {
                selectedRows = [currentRowSelectionId];
            }
        } else {
            if (event.ctrlKey === true || this._commandKeyDown === true) { // Control/Command Click
                let selectionExists = false;
                selectedRows.forEach((row, index) => {
                    if (currentRowSelectionId === row) {
                        selectionExists = true;
                        selectedRows.splice(index, 1);
                    }
                });
                if (!selectionExists) {
                    selectedRows.push(currentRowSelectionId);
                }
            } else if (event.shiftKey === true) { // Shift Click
                if (currentRowSelectionId === this._lastRowSelectionId || currentRowSelectionId === selectedRows[0]) {
                    selectedRows = [currentRowSelectionId];
                } else {
                    let selectedItem = selectedRows[0]; // use first selected element
                    let rowKeys = Object.keys(this._model.getRows());
                    let currentItemIndex = rowKeys.indexOf(currentRowSelectionId);
                    let firstSelectedItemIndex = rowKeys.indexOf(selectedRows[0]);
                    if(currentItemIndex < firstSelectedItemIndex){ // if current row is higer than the selection, use the last selected element
                        selectedItem = selectedRows[selectedRows.length - 1];
                    }
                    selectedRows = this._getRangeSelection(currentRowSelectionId, selectedItem);
                }
            } else { // Regular Click
                selectedRows = [currentRowSelectionId];
            }
        }

        this._lastRowSelectionId = currentRowSelectionId;
        this._model.setSelectedRows(selectedRows);
        this._model._triggerUserSelectedRows(selectedRows);
    }

    /**
     * Fired when the user has double clicked a row
     * @param rowData {object}
     * @param event {event}
     * @private
     */
    _onRowUserDoubleClicked(rowData, event){
        let currentRowSelectionId = event.currentTarget.component.getId();
        this._model._triggerUserRowDoubleClicked(currentRowSelectionId);
    }

    /**
     * Fired when the user has selected an action in the cell
     * @param eventData
     * @private
     */
    _onRowAction(eventData){
        this._model._triggerGridAction(eventData);
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/

    /**
     * Set the DOM id
     * @param newVal {string}
     * @private
     */
    _setId(newVal){
        this._rootElem.classList.add(newVal);
        this._rootElem.dataset.id = newVal;
    }

    /**
     * Build the column display
     * @param newVal {object}
     * @private
     */
    _setColumns(newVal){
        let columnOrder = this._model.getColumnOrder();
        for (let colKey in this._columnComponents) {
            this._columnComponents[colKey].removeAllListeners('UserSorted');
            this._columnComponents[colKey].shutdown();
        }
        this._columnComponents = {};
        this._hasCentralColumn = false;
        if (columnOrder.length > 0) {
            columnOrder.forEach((columnKey) => {
                let column = new GridColumn(newVal[columnKey]);
                    column.setId(columnKey);
                    column.setShowHeader(this._model.getShowHeader());
                    column.on('UserSorted', (sorted) => {
                        this._onColumnUserSorted(sorted, column);
                    });
                if (column.getSort() !== '') {
                    this._sortColumn = columnKey;
                }
                if (!_.isUndefined(newVal[columnKey].centralColumn) && newVal[columnKey].centralColumn === true) {
                    this._hasCentralColumn = true;
                }
                this._columnComponents[columnKey] = column;
                this._headerRow.appendChild(column.renderer.rootElement);
            });
        } else {
            for (let columnKey in newVal) {
                let column = new GridColumn(newVal[columnKey]);
                    column.setId(columnKey);
                    column.setShowHeader(this._model.getShowHeader());
                    column.on('UserSorted', this._onColumnUserSorted.call(this, arguments, column));
                if (column.getSort() !== '') {
                    this._sortColumn = columnKey;
                }
                if (!_.isUndefined(newVal[columnKey].centralColumn) && newVal[columnKey].centralColumn === true) {
                    this._hasCentralColumn = true;
                }
                this._columnComponents[columnKey] = column;
                this._headerRow.appendChild(column.renderer.rootElement);
            }
        }
    }

    /**
     * Removes any existing sort from columns other than the passed in column
     * @param column {component}
     * @private
     */
    _resetSortDisplay(column){
        for (let colKey in this._columnComponents) {
            let col = this._columnComponents[colKey];
            if (column !== col) {
                col.setSort('');
            }
        }
    }

    /**
     * Order the column display
     * @param newVal {array}
     * @private
     */
    _setColumnOrder(/*newVal*/){
        // TODO: REORDER THE COLUMNS, IF DISPLAYING
    }

    /**
     * Build the rows
     * @param newVal {object}
     * @param triggerSort {boolean}
     * @private
     */
    _setRows(newVal, triggerSort){
        for (let rowKey in this._rowComponents) {
            this._deleteRow(rowKey);
        }
        this._rowComponents = {};
        this._gridBody.innerHTML = '';
        if (triggerSort === true && !_.isUndefined(this._sortColumn)) {
            let sortedRowKeys = this._getSortedRowOrder();
            sortedRowKeys.forEach((key) => {
                let newRow = {};
                newRow[key] = newVal[key];
                this._addRow(newRow);
            });
        } else {
            for (let rowKey in newVal) {
                let newRow = {};
                newRow[rowKey] = newVal[rowKey];
                this._addRow(newRow);
            }
        }
    }

    /**
     * Sort the grid based on the passed in column
     * @param column {object}
     * @private
     */
    _setGridSorted(column){
        this._sortColumn = column.getId();
        this._sortFunction = column.getColumnRendererComponent().SortFunction;
        this._sort = column.getSort();
        this._resetSortDisplay(column, this._sort);
        this._sortRows();
        this._model._triggerUserSorted(this._sortColumn, this._sort);
    }

    /**
     * Add a row to the end of the display
     * @param newVal {object}
     * @param insert {boolean}
     * @private
     */
    _addRow(newVal, insert){
        let rowKey = Object.keys(newVal)[0];
        let rowData = newVal[rowKey];
        let row = new GridRow({
            id: rowKey,
            columnOrder: this._model.getColumnOrder(),
            rowData: rowData,
            columnComponents: this._columnComponents
        });
        this._rowComponents[rowKey] = row;
        this._rowComponents[rowKey].addListener('RowUserSelected', this._onRowUserSelected = this._onRowUserSelected.bind(this));
        this._rowComponents[rowKey].addListener('RowUserDoubleClicked', this._onRowUserDoubleClicked = this._onRowUserDoubleClicked.bind(this));
        this._rowComponents[rowKey].addListener('RowAction', this._onRowAction = this._onRowAction.bind(this));
        if (insert === true) {
            this._insertRow(row);
        } else {
            this._gridBody.appendChild(row.renderer.rootElement);
        }
    }

    _insertRow(rowComponent){
        if (!_.isUndefined(this._sortColumn)) {
            let sortedRows = this._getSortedRowOrder(),
                foundRow = false,
                rowId = rowComponent.getId();
            for (let i=0; i < sortedRows.length; i++) {
                let rowKey = sortedRows[i];
                if (foundRow === true) {
                    this._gridBody.insertBefore(rowComponent.renderer.rootElement, this._rowComponents[rowKey].renderer.rootElement);
                    foundRow = false;
                    break;
                }
                if (rowKey === rowId) {
                    foundRow = true;
                }
            }
            if (foundRow === true) {
                this._gridBody.appendChild(rowComponent.renderer.rootElement);
            }
        } else {
            this._gridBody.appendChild(rowComponent.renderer.rootElement);
        }
    }

    /**
     * Update a row
     * @param newVal {object}
     * @private
     */
    _updateRow(newVal){
        let rowKey = Object.keys(newVal)[0];
        let rowData = newVal[rowKey];
        if (!_.isUndefined(this._rowComponents[rowKey])) {
            this._rowComponents[rowKey].setRowData(rowData);
            if (!_.isUndefined(this._sortColumn)) {
                this._rowComponents[rowKey].renderer.rootElement.remove();
                this._insertRow(this._rowComponents[rowKey]);
            }
        }
    }

    /**
     * Delete a row
     * @param rowKey {string}
     * @private
     */
    _deleteRow(rowKey){
        if (!_.isUndefined(this._rowComponents[rowKey])) {
            this._rowComponents[rowKey].removeListener('RowUserSelected', this._onRowUserSelected);
            this._rowComponents[rowKey].removeListener('RowUserDoubleClicked', this._onRowUserDoubleClicked);
            this._rowComponents[rowKey].removeListener('RowAction', this._onRowAction);
            this._rowComponents[rowKey].shutdown();
            delete this._rowComponents[rowKey];
        }
    }

    /**
     * Sort the grid based on current sort info
     * @private
     */
    _sortRows(){
        if (!_.isUndefined(this._sortColumn)) {
            let newRowOrder = this._getSortedRowOrder(),
                newRows = {},
                oldRows = this._model.getRows();
            newRowOrder.forEach((rowKey) => {
                newRows[rowKey] = oldRows[rowKey];
            });
            this._model.setRows(newRows);
            this._model.setSelectedRows(this._model.getSelectedRows());
        }
    }

    /**
     * Get the sorted row key values
     * @returns {array}
     * @private
     */
    _getSortedRowOrder(){
        let sortArray = [],
            rowOrder = [],
            rows = this._model.getRows();
        for (let row in rows) {
            sortArray.push([row, rows[row][this._sortColumn]]);
        }
        sortArray.sort(this._sortFunction);
        if (this._sort.toLowerCase() === 'asc') {
            sortArray.forEach((row, index) => {
                rowOrder.push(sortArray[index][0]);
            });
        } else {
            for (let i = sortArray.length -1; i >= 0; i--) {
                let rowKey = sortArray[i][0];
                rowOrder.push(rowKey);
            }
        }
        return rowOrder;
    }

    /**
     * Select the passed rows
     * @param newVal {array}
     * @private
     */
    _setSelectedRows(newVal){
        for (let rowKey in this._rowComponents) {
            this._rowComponents[rowKey].setSelected(false);
        }
        newVal.forEach((rowId) => {
            this._rowComponents[rowId].setSelected(true);
        });
    }

    /**
     * Set the min and max width based on the scrollWidth if it is the central column
     * @private
     */
    _setCentralColumnWidth(){
        setTimeout(() => {
            let centralCells = this._rootElem.querySelectorAll('.central');
            // Must account for always visible sort column in header
            if (centralCells.length > 1) {
                StylesheetUtility().setStyleValue("." + this._model.getId() + " .central", 'min-width', 'auto');
                StylesheetUtility().setStyleValue("." + this._model.getId() + " .central", 'max-width', 'auto');
                let maxWidth = 0;
                centralCells.forEach((cell) => {
                    let scrollWidth = cell.scrollWidth;
                    if (scrollWidth > maxWidth) {
                        maxWidth = scrollWidth;
                    }
                });
                StylesheetUtility().setStyleValue("." + this._model.getId() + " .central", 'min-width', maxWidth + 'px');
                StylesheetUtility().setStyleValue("." + this._model.getId() + " .central", 'max-width', maxWidth + 'px');
            }
        }, 0);
    }

    /**
     * Pass the value to each column
     * @param newVal {boolean}
     * @private
     */
    _setShowHeader(newVal){
        for (let columnKey in this._columnComponents) {
            this._columnComponents[columnKey].setShowHeader(newVal);
        }
    }

    /**
     * Set or remove focus on the textfield
     * @param newVal {boolean}
     * @private
     */
    _setFocus(newVal){
        if (newVal === true) {
            this._rootElem.focus();
        } else {
            this._rootElem.blur();
        }
    }


    _getRangeSelection(currentSelection, previousSelection){
        let inSelection = false;
        let selection = [];
        if (!_.isUndefined(this._sortColumn)) {
            let sortedRowKeys = this._getSortedRowOrder();
            sortedRowKeys.forEach((rowKey) => {
                if (rowKey === currentSelection || rowKey === previousSelection) {
                    if (inSelection === false) {
                        inSelection = true;
                    } else {
                        selection.push(rowKey);
                        inSelection = false;
                    }
                }
                if (inSelection === true) {
                    selection.push(rowKey);
                }
            });
        } else {
            for (let row in this._rowComponents) {
                if (row === currentSelection || row === previousSelection) {
                    if (inSelection === false) {
                        inSelection = true;
                    } else {
                        selection.push(row);
                        inSelection = false;
                    }
                }
                if (inSelection === true) {
                    selection.push(row);
                }
            }
        }
        return selection;
    }



    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = GridRenderer;